import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';

import { ApiEndpoints } from '../constants/api-endpoints';
import { ApiService } from './api.service';
import { TokenStorageService } from './token-storage.service';

export interface UserAccessItem {
  allowView?: boolean;
  icon?: string | null;
  menuPosition?: number | null;
  menuText?: string | null;
  path?: string | null;
  allowCreate?: boolean;
  parentMenuId?: number | null;
  service?: string | null;
  menuType?: number | null;
  id?: number | null;
  componentName?: string | null;
  allowUpdate?: boolean;
  status?: number | null;
}

export interface AccessNavItem {
  id: number;
  label: string;
  path: string;
  service: string;
  menuPosition: number;
}

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  private readonly itemsSubject = new BehaviorSubject<UserAccessItem[]>([]);
  private readonly publicAuthenticatedPaths = new Set(['help-user-manual']);
  private loadRequest$: Observable<UserAccessItem[]> | null = null;
  private initialized = false;

  readonly items$ = this.itemsSubject.asObservable();

  constructor(
    private readonly api: ApiService,
    private readonly tokens: TokenStorageService
  ) {}

  ensureLoaded(force = false): Observable<UserAccessItem[]> {
    if (!this.tokens.hasActiveSession) {
      this.clear();
      return of([]);
    }

    if (!force && this.initialized) {
      return of(this.itemsSubject.value);
    }

    if (!force && this.loadRequest$) {
      return this.loadRequest$;
    }

    const request$ = this.api.postHandler(ApiEndpoints.USER_ACCESS_BY_ROLE, {}).pipe(
      map((res) => (Array.isArray(res?.data) ? res.data : [])),
      tap((items) => {
        this.itemsSubject.next(items);
        this.initialized = true;
      }),
      catchError((error) => {
        this.clear();
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadRequest$ = null;
      }),
      shareReplay(1)
    );

    this.loadRequest$ = request$;
    return request$;
  }

  refresh(): Observable<UserAccessItem[]> {
    this.initialized = false;
    return this.ensureLoaded(true);
  }

  clear(): void {
    this.initialized = false;
    this.loadRequest$ = null;
    this.itemsSubject.next([]);
  }

  get navItems(): AccessNavItem[] {
    return this.itemsSubject.value
      .filter((item) => Number(item?.menuType) === 2 && Number(item?.menuPosition ?? 0) > 0)
      .sort((a, b) => Number(a?.menuPosition ?? 0) - Number(b?.menuPosition ?? 0))
      .map((item) => ({
        id: Number(item?.id ?? 0),
        label: `${item?.menuText ?? ''}`.trim(),
        path: this.normalizePath(item?.path),
        service: `${item?.service ?? ''}`.trim(),
        menuPosition: Number(item?.menuPosition ?? 0)
      }))
      .filter((item) => item.id > 0 && item.label.length > 0 && item.path.length > 0);
  }

  hasService(service: string): boolean {
    const target = `${service ?? ''}`.trim();
    if (!target) {
      return false;
    }

    return this.itemsSubject.value.some((item) => `${item?.service ?? ''}`.trim() === target);
  }

  canAccessPath(path: string): boolean {
    const normalizedPath = this.normalizePath(path);
    if (!normalizedPath || normalizedPath === 'signIn' || normalizedPath === 'signUp') {
      return true;
    }

    if (this.publicAuthenticatedPaths.has(normalizedPath)) {
      return true;
    }

    return this.itemsSubject.value.some((item) => this.normalizePath(item?.path) === normalizedPath);
  }

  defaultRoute(): string {
    const firstVisible = this.navItems[0];
    if (firstVisible?.path) {
      return `/${firstVisible.path}`;
    }

    const firstAccessiblePath = this.itemsSubject.value
      .map((item) => this.normalizePath(item?.path))
      .find((path) => !!path && path !== 'signIn' && path !== 'signUp');

    if (firstAccessiblePath) {
      return `/${firstAccessiblePath}`;
    }

    return this.tokens.hasActiveSession ? '/dashboard' : '/signIn';
  }

  private normalizePath(path: unknown): string {
    return `${path ?? ''}`.trim().replace(/^\/+/, '');
  }
}
